import React from 'react';
import { motion } from 'framer-motion';
import {
  FaServer,
  FaUsers,
  FaHeart,
  FaGraduationCap,
  FaHandsHelping,
  FaTrophy,
  FaRocket,
  FaCode
} from 'react-icons/fa';
import BlurText from './ui/BlurText';

const WhatWeBuild = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const whatWeBuild = [
    {
      icon: FaGraduationCap,
      title: "Campus Portals",
      description: "Comprehensive systems for attendance, event management, and administrative workflows.",
      color: "from-blue-400 to-cyan-300"
    },
    {
      icon: FaServer,
      title: "Department Websites",
      description: "Modern, responsive web presence for academic departments with integrated CMS.",
      color: "from-purple-400 to-pink-300"
    },
    {
      icon: FaRocket,
      title: "Automation Tools",
      description: "Smart workflows that digitize manual processes and boost campus efficiency.",
      color: "from-orange-400 to-red-300"
    },
    {
      icon: FaHeart,
      title: "Social Impact Apps",
      description: "Technology solutions empowering NGOs and community welfare initiatives.",
      color: "from-green-400 to-emerald-300"
    }
  ];

  const whoWeBuildFor = [
    {
      icon: FaGraduationCap,
      title: "Departments",
      description: "Academic & Admin Units"
    },
    {
      icon: FaUsers,
      title: "Student Clubs",
      description: "Cells & Organizations"
    },
    {
      icon: FaHandsHelping,
      title: "NGOs",
      description: "Social Initiatives"
    },
    {
      icon: FaTrophy,
      title: "Events",
      description: "Hackathons & Fests"
    }
  ];

  const impactStats = [
    {
      icon: FaCode,
      value: "10+",
      label: "Live Projects",
      desc: "Deployed in Production"
    },
    {
      icon: FaUsers,
      value: "1000+",
      label: "Active Users",
      desc: "Students & Faculty"
    },
    {
      icon: FaServer,
      value: "99.9%",
      label: "Uptime",
      desc: "Reliable Infrastructure"
    }
  ];

  return (
    <section className="relative py-32 bg-dark overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] mix-blend-screen animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[120px] mix-blend-screen animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald/10 rounded-full blur-[150px] mix-blend-screen pointer-events-none" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-white/5 border border-white/10 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
              Our Expertise
            </span>
            <BlurText
              text="What We Build"
              className="text-5xl md:text-7xl font-bold mb-6 tracking-tight text-center"
              childClassName="gradient-text"
              delay={150}
              animateBy="words"
              direction="top"
            />
            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Crafting digital experiences that transform how our campus connects, learns, and grows.
            </p>
          </motion.div>
        </div>

        {/* Main Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-32"
        >
          {whatWeBuild.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative h-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-white/20 transition-colors duration-300 overflow-hidden">
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${item.color} opacity-10 blur-2xl rounded-bl-full transform translate-x-10 -translate-y-10 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500`} />

                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} p-[1px] mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <div className="w-full h-full bg-dark rounded-2xl flex items-center justify-center">
                      <item.icon className="text-2xl text-white" />
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Who We Build For Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Who We Build For</h3>
            <div className="h-1 w-20 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8"
          >
            {whoWeBuildFor.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-12 h-12 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-4 text-primary group-hover:text-white group-hover:bg-primary transition-colors duration-300">
                    <item.icon className="text-xl" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-400">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Impact Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-[3rem] overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-emerald/10 backdrop-blur-3xl" />
          <div className="absolute inset-0 border border-white/10 rounded-[3rem]" />

          <div className="relative z-10 grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {impactStats.map((stat, index) => (
              <div key={index} className="p-12 text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="text-3xl text-accent" />
                </div>
                <div className="text-5xl md:text-6xl font-black text-white mb-2 tracking-tight">
                  {stat.value}
                </div>
                <div className="text-xl font-bold text-primary mb-2">{stat.label}</div>
                <div className="text-gray-400">{stat.desc}</div>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default WhatWeBuild;
